import React, { FC, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { formatListingMedia, listingMediaIsVideo } from 'utils/listings';
import { ReschedulableBooking } from '@kouto/types';
import useGuestReschedule from 'features/GuestReschedule/hooks/useGuestReschedule';
import { formatSessionLabel } from 'utils/date/formatSessionLabel';
import { formatSessionLabelWithTimezone } from 'features/GuestReschedule/formatSessionLabelWithTimezone';

interface BookingItemCardProps {
  readonly booking: ReschedulableBooking;
  readonly rescheduledDate?: string;
  readonly isSelectable?: boolean;
}

const ReservationItem: FC<BookingItemCardProps> = ({
  booking,
  rescheduledDate,
  isSelectable,
}) => {
  const radioRef = useRef<HTMLInputElement>(null);
  const { selectedBookingId, setSelectedBookingId } = useGuestReschedule();

  const handleCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedBookingId(booking.bookingId);
    }
  };

  const photoUrl = useMemo(() => {
    if (!booking.coverMedia?.links?.length) {
      return '';
    }

    const formattedMedia = formatListingMedia([booking.coverMedia])[0];
    if (listingMediaIsVideo(formattedMedia)) {
      return formattedMedia.previewMobile;
    }
    return formattedMedia.urlMobile;
  }, [booking]);

  return (
    <Wrapper
      data-testid={booking.bookingId}
      selectable={!!isSelectable}
      onClick={() => radioRef.current?.click()}
    >
      <Image src={photoUrl} />
      <Content>
        <Title>{booking.experienceTitle}</Title>
        <Row>
          <Details
            style={{
              textDecoration: rescheduledDate ? 'line-through' : 'none',
            }}
          >
            {formatSessionLabelWithTimezone(
              booking.eventStartDateTime as unknown as string,
              booking.eventSessionDurationInMinutes,
              booking.experienceTimeZoneId,
            )}
          </Details>
          {rescheduledDate && (
            <>
              <Dot>•</Dot>
              <Details>
                {formatSessionLabel(
                  rescheduledDate,
                  booking.eventSessionDurationInMinutes,
                )}
              </Details>
            </>
          )}
        </Row>
      </Content>
      <StatusWrapper>
        {isSelectable && (
          <input
            ref={radioRef}
            type="radio"
            name="booking"
            checked={selectedBookingId === booking.bookingId}
            onChange={handleCheck}
          />
        )}
      </StatusWrapper>
    </Wrapper>
  );
};

export default ReservationItem;

const Wrapper = styled.div<{ selectable: boolean }>`
  position: relative;
  width: 100%;
  height: 92px;
  cursor: default;
  user-select: none;
  border: 0.5px solid var(--way-colors-borderColor);
  transition-duration: 0.2s;
  display: flex;
  align-items: center;
  flex-direction: row;

  ${({ selectable }) =>
    selectable
      ? `
        cursor: pointer;
        &:hover {
          border: solid 0.5px var(--way-colors-borderHoverColor);
        }
    `
      : ''}
`;

const Image = styled.div<{ src: string }>`
  height: 100%;
  top: 0px;
  left: 0px;
  z-index: 10;
  aspect-ratio: 1;
  background: url('${({ src }) => src}') lightgray 50% / cover no-repeat;
`;

const Content = styled.div`
  padding: 20px;
  height: 100%;
  width: 100%;
`;

const Title = styled.h3`
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  overflow: hidden;
  letter-spacing: 0.016px;
  text-overflow: ellipsis;
  margin: 0px;
  color: var(--way-colors-primaryTextColor);
`;

/* stylelint-disable value-no-vendor-prefix, property-no-vendor-prefix */
const Details = styled.p`
  margin: 0px;
  margin-top: 12px;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 0.014px;
  color: var(--way-colors-contrastColorShades-60);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;
/* stylelint-enable value-no-vendor-prefix, property-no-vendor-prefix */

const StatusWrapper = styled.div`
  position: relative;
  display: grid;
  place-content: center;
  padding-right: 20px;
  flex-shrink: 0;

  & > input[type='radio'] {
    width: 18px;
    height: 18px;
    accent-color: var(--way-colors-contrastColorShades-80);
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
  align-items: center;
`;

const Dot = styled.span`
  font-size: 20px;
  margin-top: 11px;
  color: var(--way-colors-contrastColorShades-60);
`;
